import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import { getFormularioSecretariaMunicipalAgriculturaByUUID } from 'api/forms/pacote_formularios.api';
import ContainerPainel from 'components/container_painel';
import { FormAgriculturaHorizontalStepper } from 'components/forms/form_agricultura_horizontal_stepper';
import { Loading } from 'components/login';
import { useAuth } from 'contexts/auth/auth_context';
import { UserPermissionEnum } from 'enum/userPermission.enum';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ROUTES } from 'routes/constants';
import { useFormularioSecretariaMunicipalAgriculturaStore } from 'stores/forms/formularioAgricultura.store';
import Step1View from './steps/step1_view';
import Step2View from './steps/step2_view';
import Step3View from './steps/step3_view';
import Step4View from './steps/step4_view';
import Step5View from './steps/step5_view';
import Step6View from './steps/step6_view';
import Step7View from './steps/step7_view';
import Step8View from './steps/step8_view';

const FormularioSecretariaMunicipalAgriculturaVisualizar: React.FC = () => {
  const { uuid } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const usuariosPermitidos = [
    UserPermissionEnum.STATE_MANAGER,
    UserPermissionEnum.MUNICIPAL_MANAGER,
    UserPermissionEnum.ADMIN, // TODO: remover, liberado apenas por enquanto para teste
  ];

  const isUsuarioPermitido = usuariosPermitidos.includes(
    user?.permission.name as UserPermissionEnum,
  );

  const { formulario, setFormulario } =
    useFormularioSecretariaMunicipalAgriculturaStore();

  const getFormulario = async () => {
    try {
      setHasError(false);
      setErrorMessage('');

      if (!uuid) {
        setHasError(true);
        setErrorMessage('UUID do formulário não encontrado');
        return;
      }

      const response = await getFormularioSecretariaMunicipalAgriculturaByUUID(uuid);

      if (!response?.dados) {
        setHasError(true);
        setErrorMessage('Município sem informação enviada para visualização pelo Gerente Estadual');
        return;
      }

      // Verificar se há dados preenchidos
      const temDadosPreenchidos = Object.values(response.dados).some(value =>
        value !== null && value !== undefined && value !== '' &&
        (Array.isArray(value) ? value.length > 0 : true)
      );

      if (!temDadosPreenchidos) {
        setHasError(true);
        setErrorMessage('Nenhum dado preenchido disponível para este município');
        return;
      }

      setFormulario(response.dados);
    } catch (error) {
      console.error('Erro ao buscar formulário:', error);
      setHasError(true);
      setErrorMessage('Município sem informação enviada para visualização pelo Gerente Estadual');
    }
  };

  useEffect(() => {
    if (uuid) {
      getFormulario();
    }

    // Cleanup ao sair da tela
    return () => {
      setFormulario(null);
    };
  }, [uuid]);

  const renderStepContent = () => {
    if (hasError) {
      return (
        <Box display="flex" justifyContent="center" mt={3}>
          <Alert severity="warning" sx={{ maxWidth: 600 }}>
            <Typography variant="body1">
              {errorMessage}
            </Typography>
          </Alert>
        </Box>
      );
    }

    if (!formulario) {
      return (
        <Box display="flex" justifyContent="center">
          <Loading />
        </Box>
      );
    }

    // Renderizar o step atual
    switch (currentStep) {
      case 1:
        return <Step1View />;
      case 2:
        return <Step2View />;
      case 3:
        return <Step3View />;
      case 4:
        return <Step4View />;
      case 5:
        return <Step5View />;
      case 6:
        return <Step6View />;
      case 7:
        return <Step7View />;
      case 8:
        return <Step8View />;
      default:
        return <Step1View />;
    }
  };

  const getStepTitle = (step: number) => {
    const titles = [
      'Informações Gerais',
      'Informações Complementares',
      'Órgãos Estaduais',
      'Serviços Prestados',
      'Estrutura',
      'Servidores',
      'Famílias Atendidas',
      'Assistência Técnica'
    ];
    return titles[step - 1] || 'Informações';
  };

  return (
    <ContainerPainel
      pagina='Visualizar Formulário da Secretaria Municipal de Agricultura'
      showBackButton={true}
    >
      {isUsuarioPermitido ? (
        <Stack marginX={'4rem'} paddingTop={'2rem'}>
          <FormAgriculturaHorizontalStepper
            activeStep={currentStep}
            edit={false}
          />

          <Box mt={2}>
            {renderStepContent()}
          </Box>

          {/* Navegação entre abas */}
          {!hasError && formulario && (
            <Box display="flex" justifyContent="space-between" mt={4} mb={2}>
              <Button
                variant="outlined"
                onClick={() => setCurrentStep(Math.max(1, currentStep - 1))}
                disabled={currentStep === 1}
              >
                Aba Anterior
              </Button>

              <Typography variant="body2" color="textSecondary" alignSelf="center">
                Aba {currentStep} de 8
              </Typography>

              <Button
                variant="outlined"
                onClick={() => setCurrentStep(Math.min(8, currentStep + 1))}
                disabled={currentStep === 8}
              >
                Próxima Aba
              </Button>
            </Box>
          )}
        </Stack>
      ) : (
        <Stack
          sx={{
            marginX: '4rem',
            paddingTop: '2rem',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            backgroundColor: '#FFF',
            borderRadius: '1rem',
            padding: '2rem',
            margin: '2rem',
          }}
        >
          <Typography variant="h6" color="error">
            Acesso Negado
          </Typography>
          <Typography>
            Você não tem permissão para visualizar este formulário
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate(ROUTES.PACOTE_FORMULARIOS)}
          >
            Voltar
          </Button>
        </Stack>
      )}
    </ContainerPainel>
  );
};

export default FormularioSecretariaMunicipalAgriculturaVisualizar;
